<template>
  <contact-page-content :title="title">
    <template #subtitle>
      <p v-if="metadata.origin_page_action === 'apply_cta'" class="mb-6">
        L'équipe Pollen reviendra vers vous après acceptation de votre demande.
      </p>
      <figure v-if="data.teacher" class="flex gap-6">
        <app-user-avatar
          :avatar-url="data.teacher.avatar_url"
          class="h-[120px] w-[120px] md:h-auto md:w-[160px]"
          :name="data.teacher.full_name!"
          :size="160"
          tile
        />

        <figcaption>
          <p class="mb-2 text-xl font-bold">
            {{ data.course.title }}
          </p>
          <p class="text-lg">
            <strong>{{ data.teacher.full_name }}</strong> -
            <span class="mr-2">{{ data.teacher.job }}</span>
            <teacher-companies-logos
              :companies="data.teacher.companies"
              :height="24"
            />
          </p>

          <div class="mt-4 flex flex-wrap gap-2">
            <course-session-date-chip :session="data" time />
            <place-address-chip
              v-if="data.place"
              :address="data.place.address"
              field="city"
            />
          </div>
        </figcaption>
      </figure>

      <template v-else>
        <p class="mb-2 text-xl font-bold">
          {{ data.course.title }}
        </p>

        <div class="mb-6 flex flex-wrap gap-2">
          <course-session-date-chip :session="data" size="small" time />
          <place-address-chip
            v-if="data.place"
            :address="data.place.address"
            field="city"
            size="small"
          />
        </div>

        <ul
          class="flex flex-wrap items-center gap-x-4 gap-y-2 text-base font-normal"
        >
          <li v-for="teacher in data.teachers" :key="teacher.id">
            <figure class="grid grid-cols-[auto_1fr] gap-2">
              <app-user-avatar
                :avatar-url="teacher.avatar_url"
                :name="teacher.full_name"
                :size="36"
                tile
              />

              <figcaption>
                <p class="font-semibold">
                  {{ teacher.full_name }}
                </p>
                <p class="mb-1 text-sm">
                  {{ teacher.job }}
                </p>

                <p>
                  <teacher-companies-logos
                    :companies="teacher.companies"
                    :height="20"
                  />
                </p>
              </figcaption>
            </figure>
          </li>
        </ul>
      </template>
    </template>

    <contact-page-content-feature-list
      v-if="data.teacher"
      class="hidden lg:block"
      :list="features"
      title="Les + d'un évènement Pollen"
    />
  </contact-page-content>
</template>

<script setup lang="ts">
import type { I18nMessage } from "@pollen/core/model/i18n.model";

import ContactPageContent from "~/pages/contact/_includes/components/content/contact-page-content.vue";
import ContactPageContentFeatureList from "~/pages/contact/_includes/components/content/contact-page-content-feature-list.vue";
import type { ContactFlowMetadata } from "~~/model/analytics.model";

const properties = defineProps<{
  metadata: ContactFlowMetadata;
}>();

const { data } = await useEventView(properties.metadata.origin_page_id!);

if (data.value.is_upcoming && data.value.is_sold_out) {
  await navigateTo(data.value._links.page);
}

const features: { label: I18nMessage; icon: string }[] = [
  {
    label: "Le partage de meilleurs pratiques concrètes et actionnables",
    icon: "ph:users-four",
  },
  {
    label: "Une dose d’inspiration par les leaders du marché",
    icon: "ph:cursor-click",
  },
  {
    label: "Un échange privilégié avec la communauté Pollen",
    icon: "ph:atom",
  },
];

const title = computed(() => {
  if (properties.metadata.origin_page_action === "view_replay_cta") {
    return "Voir le replay";
  }

  return "Postulez à l'événement";
});
</script>
